/**
 * SocialKitModal — AI-generated social copy for a track or album.
 * One button in Studio → this modal → copy-paste-ready posts for every platform.
 *
 * The parent tells us what to generate for: pass `kind` ("track"|"album") + `id`.
 * We call the LLM lazily on first open, cache the result in state so users can
 * flip between platforms without re-billing an LLM call.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import {
  Sparkles, Copy, Instagram, Facebook, Twitter, Music2, RefreshCw, Loader2, User,
  ExternalLink, HelpCircle, BookOpen, Zap,
} from "lucide-react";

const PLATFORMS = [
  { key: "instagram", label: "Instagram", icon: Instagram, hint: "Caption + hashtags. Paste into a post or Reel.",
    postUrl: "https://www.instagram.com/", buttonLabel: "Open Instagram" },
  { key: "facebook", label: "Facebook", icon: Facebook, hint: "Includes the link inline. Paste as a status.",
    postUrl: "https://www.facebook.com/", buttonLabel: "Open Facebook" },
  { key: "x", label: "X (Twitter)", icon: Twitter, hint: "Short + punchy. Fits in one tweet.",
    // X supports a pre-filled composer via ?text=... — we inject the caption.
    postUrl: (text) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text || "")}`,
    buttonLabel: "Open X composer" },
  { key: "tiktok", label: "TikTok", icon: Music2, hint: "TikTok strips links — paste this into your caption, put the URL in your bio.",
    postUrl: "https://www.tiktok.com/upload", buttonLabel: "Open TikTok" },
];

// Hook-style tabs used when variants have loaded. Kept as its own constant so
// the render loop is trivially memoized.
const VARIANT_TABS = [
  { key: "question",  label: "Question",  icon: HelpCircle,
    hint: "Opens with a hook question — great for engagement." },
  { key: "story",     label: "Story",     icon: BookOpen,
    hint: "Opens with a scene or personal detail — draws readers in." },
  { key: "punchline", label: "Punchline", icon: Zap,
    hint: "Bold one-line claim front and center — high energy." },
];

export default function SocialKitModal({ kind, id, title, onClose }) {
  const [kit, setKit] = useState(null);        // single-shot base kit
  const [variants, setVariants] = useState(null); // {platform: {question,story,punchline}}
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("instagram");
  const [style, setStyle] = useState("story"); // active hook style
  const platformsForTab = useMemo(() => PLATFORMS.filter((p) => p.key === tab), [tab]);

  const load = async () => {
    setBusy(true);
    try {
      // Fire both requests in parallel so the modal is fully populated in one wait.
      const [baseResp, varResp] = await Promise.all([
        api.post(`/social-kit/${kind}/${id}`),
        api.post(`/social-kit/${kind}/${id}/variants`).catch(() => null),
      ]);
      setKit(baseResp.data);
      if (varResp && varResp.data) setVariants(varResp.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Couldn't copy"); }
  };

  // "Copy + Open Platform" — clipboards the text, then opens the platform's
  // post composer in a new tab. For X we deep-link to /intent/tweet with the
  // caption pre-filled; other platforms don't support pre-fill so we just get
  // the user to the composer.
  const copyAndOpen = async (text, platform) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); }
    catch { /* proceed even if clipboard blocked */ }
    const url = typeof platform.postUrl === "function" ? platform.postUrl(text) : platform.postUrl;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`Copied — pasting into ${platform.label}`);
  };

  // The text to show + copy for the current platform+style. Falls back to the
  // one-shot kit if the variants call failed.
  const activeText = variants
    ? (variants[tab]?.[style] || kit?.[tab] || "")
    : (kit?.[tab] || "");

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="social-kit-modal" className="rounded-none bg-[#0A0A0A] border-[#2A2A2A] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#FF4500]" />
            AI Social Kit · "{title}"
          </DialogTitle>
          <div className="font-mono text-[11px] text-gray-500 mt-1">
            Copy-paste-ready posts for every platform, written from your track's info. No fees, no signups.
          </div>
        </DialogHeader>

        {busy && !kit && (
          <div className="py-12 text-center font-mono text-xs text-gray-400 uppercase tracking-widest">
            <Loader2 className="h-6 w-6 mx-auto mb-3 animate-spin text-[#FF4500]" />
            Writing your posts...
          </div>
        )}

        {kit && (
          <>
            {/* Platform tabs */}
            <div className="flex flex-wrap gap-1 border-b border-[#2A2A2A] -mx-6 px-6 mt-2">
              {PLATFORMS.map((p) => {
                const Icon = p.icon;
                const active = tab === p.key;
                return (
                  <button
                    key={p.key}
                    data-testid={`social-tab-${p.key}`}
                    onClick={() => setTab(p.key)}
                    className={`flex items-center gap-2 px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors border-b-2 ${
                      active ? "border-[#FF4500] text-[#FF4500]" : "border-transparent text-gray-400 hover:text-white"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {p.label}
                  </button>
                );
              })}
            </div>

            {/* Selected platform */}
            {platformsForTab.map((p) => (
              <div key={p.key} className="mt-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">{p.hint}</div>

                {/* Hook style tabs (only render when variants loaded) */}
                {variants && (
                  <div className="flex gap-1 mb-2 border border-[#2A2A2A] bg-black/40 p-1">
                    {VARIANT_TABS.map((s) => {
                      const SIcon = s.icon;
                      const active = style === s.key;
                      return (
                        <button
                          key={s.key}
                          data-testid={`variant-tab-${s.key}`}
                          onClick={() => setStyle(s.key)}
                          className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                            active ? "bg-[#FF4500] text-black" : "text-gray-400 hover:text-white"
                          }`}
                          title={s.hint}
                        >
                          <SIcon className="h-3 w-3" /> {s.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div
                  data-testid={`social-text-${p.key}`}
                  className="border border-[#2A2A2A] bg-black/40 p-4 font-mono text-sm text-gray-100 whitespace-pre-wrap leading-relaxed"
                >
                  {activeText || <span className="text-gray-500">Nothing here — hit Regenerate.</span>}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button
                    data-testid={`social-copy-${p.key}`}
                    onClick={() => copy(activeText, p.label)}
                    disabled={!activeText}
                    variant="outline"
                    className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-10"
                  >
                    <Copy className="h-3.5 w-3.5 mr-2" /> Copy only
                  </Button>
                  <Button
                    data-testid={`social-copy-open-${p.key}`}
                    onClick={() => copyAndOpen(activeText, p)}
                    disabled={!activeText}
                    className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-10"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-2" /> Copy + {p.buttonLabel}
                  </Button>
                </div>
              </div>
            ))}

            {/* Bios section — always visible below the platform tab */}
            <div className="mt-6 pt-5 border-t border-[#2A2A2A]">
              <div className="flex items-center gap-2 mb-3">
                <User className="h-4 w-4 text-[#FF4500]" />
                <span className="font-mono text-xs uppercase tracking-widest text-gray-300">
                  Artist bio · three lengths for different profiles
                </span>
              </div>
              <BioRow label="Instagram bio · 140 chars" testid="bio_short" text={kit.bio_short} onCopy={copy} />
              <BioRow label="X bio · 160 chars" testid="bio_medium" text={kit.bio_medium} onCopy={copy} />
              <BioRow label="Facebook / website · long" testid="bio_long" text={kit.bio_long} onCopy={copy} />
            </div>

            <div className="mt-6 flex justify-between items-center">
              <div className="font-mono text-[10px] text-gray-500">
                AI-written — feel free to tweak before posting.
              </div>
              <Button
                data-testid="social-regenerate"
                onClick={load}
                disabled={busy}
                variant="outline"
                className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-9 px-4"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-2" />}
                Regenerate
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BioRow({ label, testid, text, onCopy }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">{label}</div>
      <div className="flex gap-2">
        <div
          data-testid={`social-text-${testid}`}
          className="flex-1 border border-[#2A2A2A] bg-black/40 px-3 py-2 font-mono text-xs text-gray-100 leading-relaxed"
        >
          {text || <span className="text-gray-500">—</span>}
        </div>
        <button
          data-testid={`social-copy-${testid}`}
          onClick={() => onCopy(text, "Bio")}
          disabled={!text}
          className="h-9 w-9 grid place-items-center border border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] disabled:opacity-30"
          title="Copy"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
